
 <template>
  <div class="news-management" dir="rtl">
    <div class="container">
      <!-- Header Section -->
      <header class="header">
        <h1 class="page-title">إدارة الأخبار</h1>
        <p class="page-subtitle">قم بإدارة وتحديث أخبار المنصة بسهولة</p>
        <button class="btn-add" @click="openModalForAdd">
          <span class="btn-icon">+</span>
          إضافة خبر جديد
        </button>
      </header>

      <!-- News Grid -->
      <section class="cards-grid">
        <div v-for="news in newsList" :key="news.id" class="news-card">
          <div class="card-image-container">
            <img
              :src="news.imageFullUrl || defaultImage"
              :alt="news.title"
              class="news-image"
              @error="handleImageError"
            />
            <div class="image-overlay">
              <div class="overlay-content">
                <span class="date-badge">
                  {{ formatDate(news.createdAt) }}
                </span>
              </div>
            </div>
          </div>

          <div class="card-content">
            <h3 class="news-title">{{ news.title }}</h3>
            <p class="news-content">
              {{ truncateText(news.theContent, 150) }}
            </p>
          </div>

          <div class="card-actions">
            <button class="btn-action edit" @click="openModalForEdit(news)">
              <span class="action-icon">✎</span>
              تعديل
            </button>
            <button class="btn-action delete" @click="deleteNews(news.id)">
              <span class="action-icon">×</span>
              حذف
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="newsList.length === 0" class="empty-state">
          <div class="empty-icon">📰</div>
          <h3>لا توجد أخبار</h3>
          <p>قم بإضافة خبر جديد للبدء</p>
        </div>
      </section>

      <!-- Add/Edit Modal -->
      <transition name="modal">
        <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">
                {{ isEditMode ? "تعديل الخبر" : "إضافة خبر جديد" }}
              </h2>
              <button class="modal-close" @click="closeModal">&times;</button>
            </div>

            <form @submit.prevent="handleSubmit" class="news-form">
              <div class="form-group">
                <label class="form-label">عنوان الخبر</label>
                <input
                  type="text"
                  v-model="form.title"
                  class="form-control"
                  placeholder="أدخل عنوان الخبر"
                  required
                />
              </div>

              <div class="form-group">
                <label class="form-label">محتوى الخبر</label>
                <textarea
                  v-model="form.theContent"
                  class="form-control"
                  rows="6"
                  placeholder="أدخل محتوى الخبر"
                  required
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">صورة الخبر</label>
                <div 
                  class="file-upload-container" 
                  @dragover.prevent 
                  @drop.prevent="handleFileDrop"
                  @click="$refs.imageInput.click()"
                >
                  <input
                    type="file"
                    ref="imageInput"
                    class="file-input"
                    @change="handleFileChange"
                    accept="image/*"
                    :required="!isEditMode && !form.imageUrl"
                  />
                  
                  <div v-if="!form.image && !form.imageUrl" class="upload-placeholder">
                    <span class="upload-icon">↑</span>
                    <span>اضغط أو اسحب صورة هنا</span>
                    <span class="upload-hint">PNG, JPG حتى 5MB</span>
                  </div>

                  <div v-else class="file-preview">
                    <img 
                      :src="previewUrl || form.imageUrl" 
                      alt="معاينة" 
                      class="preview-image"
                    />
                    <button type="button" class="remove-file" @click.stop="removeFile">×</button>
                  </div>
                </div>
              </div>

              <div class="modal-actions">
                <button type="submit" class="btn-submit" :disabled="isSubmitting">
                  <span v-if="isSubmitting" class="spinner"></span>
                  {{ isEditMode ? "تحديث الخبر" : "إضافة الخبر" }}
                </button>
                <button type="button" class="btn-cancel" @click="closeModal">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>

      <!-- Pagination -->
      <div class="pagination-container">
        <custom-pagination
          :currentPage="pageNumber"
          :totalPages="totalPages"
          @page-changed="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>


<script>
import Swal from "sweetalert2";

import apiClient from "@/api";
import CustomPagination from "@/components/CustomPagination.vue";

export default {
  name: "NewsManagement",
  components: { CustomPagination },
  data() {
    return {
      newsList: [],
      pageNumber: 1,
      pageSize: 6,
      totalPages: 1,
      showModal: false,

      isEditMode: false,

      defaultImage: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999' font-family='Arial' font-size='14'%3Eلا توجد صورة%3C/text%3E%3C/svg%3E",

      form: {
        id: null,
        title: "",
        theContent: "",
        image: null,
      },
    };
  },
  methods: {

  // Add this to your methods section in the Vue component
// Add this to your methods section in the Vue component

truncateText(text, maxLength = 100, suffix = '...') {
  if (!text) return '';
  
  try {
    // Remove extra whitespace
    text = text.trim();
    
    // If text is shorter than maxLength, return it as is
    if (text.length <= maxLength) {
      return text;
    }
    
    // Cut text and make sure we don't cut in the middle of a word
    let truncated = text.substring(0, maxLength);
    let lastSpace = truncated.lastIndexOf(' ');
    
    // If there's a space in the truncated text, cut at the last space
    if (lastSpace > 0) {
      truncated = truncated.substring(0, lastSpace);
    }
    
    return truncated + suffix;
  } catch (error) {
    console.error('Error truncating text:', error);
    return text;
  }
},

// Advanced version with more options
truncateTextAdvanced(text, options = {}) {
  const defaults = {
    maxLength: 100,
    suffix: '...',
    preserveWords: true,
    stripHtml: true,
    addTooltip: false
  };

  const settings = { ...defaults, ...options };

  if (!text) return '';

  try {
    // Strip HTML if needed
    if (settings.stripHtml) {
      text = text.replace(/<[^>]*>/g, '');
    }

    // Remove extra whitespace
    text = text.trim();

    // If text is shorter than maxLength, return it as is
    if (text.length <= settings.maxLength) {
      return text;
    }

    let truncated = text.substring(0, settings.maxLength);

    if (settings.preserveWords) {
      const lastSpace = truncated.lastIndexOf(' ');
      if (lastSpace > 0) {
        truncated = truncated.substring(0, lastSpace);
      }
    }

    // Add suffix
    truncated += settings.suffix;

    // Add tooltip attribute if requested
    if (settings.addTooltip) {
      return `<span title="${text}">${truncated}</span>`;
    }

    return truncated;
  } catch (error) {
    console.error('Error truncating text:', error);
    return text;
  }
},
formatDate(dateStr) {
  if (!dateStr) return "غير محدد";
  
  try {
    const date = new Date(dateStr);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "تاريخ غير صالح";
    }

    // Format options for Arabic locale
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    // Create Arabic date formatter
    return new Intl.DateTimeFormat('ar-SA', options).format(date);

  } catch (error) {
    console.error('Error formatting date:', error);
    return "خطأ في التاريخ";
  }
},

// Alternative version with relative time
formatDateRelative(dateStr) {
  if (!dateStr) return "غير محدد";
  
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // If less than 24 hours, show relative time
    if (diffDays < 1) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        if (diffMinutes < 1) {
          return "الآن";
        }
        return `منذ ${diffMinutes} دقيقة`;
      }
      return `منذ ${diffHours} ساعة`;
    }
    
    // If less than 7 days, show days ago
    if (diffDays < 7) {
      return `منذ ${diffDays} يوم`;
    }
    
    // Otherwise show full date
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return new Intl.DateTimeFormat('ar-SA', options).format(date);
    
  } catch (error) {
    console.error('Error formatting date:', error);
    return "خطأ في التاريخ";
  }
},
    async getAllNews() {
      try {
        const response = await apiClient.get("/api/News/GetAllNews", {
          params: {
            PageNumber: this.pageNumber,
            PageSize: this.pageSize,
          },
        });

        if (response.data.items) {
          this.newsList = response.data.items;
          this.pageNumber = response.data.pageNumber;
          this.totalPages = response.data.totalPages;
        } else {
          this.newsList = response.data;
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("حدث خطأ أثناء جلب الأخبار:", error);
        alert("حدث خطأ أثناء جلب الأخبار");
      }
    },

    openModalForAdd() {
      this.resetForm();
      this.isEditMode = false;
      this.showModal = true;
    },
    handlePageChange(newPage) {
      this.pageNumber = newPage;
      this.getAllNews();
    },

    openModalForEdit(news) {
      this.isEditMode = true;
      this.form.id = news.id;
      this.form.title = news.title;
      this.form.theContent = news.theContent;
      this.form.imageUrl = news.imageFullUrl || "";

      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },

    // التعامل مع اختيار ملف الصورة
    handleFileChange(event) {
      const file = event.target.files[0];
      this.form.image = file;
    },

    resetForm() {
      this.isEditMode = false;
      this.form = {
        id: null,
        title: "",
        theContent: "",
        image: null,
      };
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = "";
      }
    },

    async handleSubmit() {
      try {
        if (!this.isEditMode) {
          const formData = new FormData();
          formData.append("Title", this.form.title);
          formData.append("TheContent", this.form.theContent);
          if (this.form.image) {
            formData.append("Image", this.form.image);
          }

          const response = await apiClient.post("/api/News/AddNews", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (response.data) {
            this.showSuccess("تمت إضافة الخبر بنجاح");
          }
        
        } else {
          const formData = new FormData();
          formData.append("Title", this.form.title);
          formData.append("TheContent", this.form.theContent);
          if (this.form.image) {
            formData.append("Image", this.form.image);
          }

          await apiClient.put("/api/News/UpdateNews", formData, {
            params: { id: this.form.id },
            headers: { "Content-Type": "multipart/form-data" },
          });
          // sweetalet
          this.showSuccess("تم تحديث الخبر بنجاح");
        
       
        }

        this.closeModal();
        this.getAllNews();
      } catch (error) {
        console.error("حدث خطأ أثناء معالجة الخبر:", error);
        this.showError("حدث خطأ أثناء معالجة الخبر"); 
       
      }
    },
    async deleteNews(id) {
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        text: "هل أنت متأكد من حذف هذا الخبر؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "نعم، احذفه",
        cancelButtonText: "إلغاء",
      });

      if (result.isConfirmed) {
        try {
          await apiClient.delete("/api/News/DeleteNews", {
            params: { id },
          });
          this.showSuccess("تم حذف الخبر بنجاح");
          this.getAllNews();
        } catch (error) {
          console.error("حدث خطأ أثناء حذف الخبر:", error);
          this.showError("حدث خطأ أثناء حذف الخبر");
        }
      }
    },
 

    nextPage() {
      this.pageNumber++;
      this.getAllNews();
    },
    prevPage() {
      if (this.pageNumber > 1) {
        this.pageNumber--;
        this.getAllNews();
      }
    },

    showSuccess(message) {
      Swal.fire({
        icon: "success",
        title: "تم",
        text: message,
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          popup: 'success-modal'
        }
      });
    },
    

  showError(message) {
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: message,
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'error-modal'
        }
      });
    },

    handleImageError(event) {
      // منع الحلقة اللانهائية - إذا كانت الصورة بالفعل هي defaultImage، لا تفعل شيء
      if (event.target.src === this.defaultImage || event.target.dataset.errorHandled === 'true') {
        event.target.style.display = 'none'; // إخفاء الصورة بدلاً من إعادة المحاولة
        event.target.onerror = null; // إزالة event handler لمنع إعادة المحاولة
        return;
      }
      // تعيين علامة لمنع إعادة المحاولة
      event.target.dataset.errorHandled = 'true';
      event.target.src = this.defaultImage;
    },
  },
  

  mounted() {
    this.getAllNews();
  },
};
</script>

<style scoped>
.news-management {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header Styles */
.header {
  text-align: center;
  margin-bottom: 3rem;
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  animation: slideDown 0.5s ease-out;
}

.page-title {
  font-size: 1.0rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-weight: 400;
}

.page-subtitle {
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-add {
  background: #4299e1;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-add:hover {
  background: #3182ce;
  transform: translateY(-1px);
}

/* Cards Grid */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.news-card {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.card-image-container {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/9;
}

.news-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.news-card:hover .news-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.7));
  display: flex;
  align-items: flex-end;
  padding: 1rem;
}

.date-badge {
  background: rgba(255, 255, 255, 0.9);
  color: #2d3748;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.card-content {
  padding: 1.5rem;
}

.news-title {
  font-size: 1.1rem;
  color: #2d3748;
  margin-bottom: 0.75rem;
  font-weight: 400;
  line-height: 1.4;
}

.news-content {
  color: #718096;
  font-size: 0.875rem;
  line-height: 1.6;
}

.card-actions {
  padding: 1rem;
  background: #f7fafc;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid #e2e8f0;
}

.btn-action {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-action.edit {
  background: #faf089;
  color: #744210;
}

.btn-action.edit:hover {
  background: #f6e05e;
}

.btn-action.delete {
  background: #fed7d7;
  color: #c53030;
}

.btn-action.delete:hover {
  background: #feb2b2;
}

/* Modal Styles */
.modal-overlay {
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 1rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-title {
  font-size: 1.5rem;
  color: #2d3748;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #718096;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #2d3748;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  outline: none;
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

/* File Upload Styles */
.file-upload-container {
  border: 2px dashed #e2e8f0;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-upload-container:hover {
  border-color: #4299e1;
  background: #ebf8ff;
}

.file-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  font-size: 1.0rem;
  color: #4299e1;
}

.upload-hint {
  font-size: 0.875rem;
  color: #718096;
}

.file-preview {
  position: relative;
  margin-top: 1rem;
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 0.5rem;
  object-fit: contain;
}

.remove-file {
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  background: #fc8181;
  color: white;
  border: none;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.remove-file:hover {
  background: #f56565;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.btn-submit {
  background: #4299e1;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-submit:hover:not(:disabled) {
  background: #3182ce;
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-cancel {
  background: #e2e8f0;
  color: #4a5568;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background: #cbd5e0;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 1rem;
  grid-column: 1 / -1;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #718096;
}

/* Spinner */
.spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Animations */
@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .news-management {
    padding: 1rem;
  }

  .header {
    padding: 1.5rem;
  }

  .page-title {
    font-size: 1.0rem;
  }

  .cards-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    padding: 1.5rem;
    margin: 1rem;
  }

  .modal-actions {
    flex-direction: column;
  }

  .btn-submit,
  .btn-cancel {
    width: 100%;
  }
}

/* RTL Specific */
[dir="rtl"] .modal-close {
  left: 1rem;
  right: auto;
}

[dir="rtl"] .remove-file {
  left: -0.5rem;
  right: auto;
}

[dir="rtl"] .card-actions {
  flex-direction: row-reverse;
}

[dir="rtl"] .modal-actions {
  flex-direction: row-reverse;
}
</style>
